<template>
  <modal id="spectrumsForm"
         v-model="visible"
         :title="$t('g-spectrumsForm:createSpectrum')"
         :callback="createSpectrum"
  >
    <small slot="modal-header-right" class="text-muted font-italic">{{ $t('g-spectrumsForm:required') | capitalize }}</small>

    <div slot="modal-body">
      <div class="form-row">
        <div class="form-group col-md-6">
          <label for="modem" class="control-label">{{ $t('g-spectrumsForm:modem') | capitalize }} *</label>
          <select id="modem"
                  v-model="modem"
                  class="form-control"
          >
            <option v-for="m in modems" :key="m.serialNumber" :value="m" :data-modem-location="m.location">
              {{ m.serialNumber }}
            </option>
          </select>
        </div>

        <div class="form-group col-md-6">
          <label for="duration" class="control-label">{{ $t('g-spectrumsForm:duration') | capitalize }} *</label>
          <input id="duration"
                 v-model="duration"
                 v-validate="'required|spectrumDuration'"
                 :data-vv-as="$t('g-spectrumsForm:duration')"
                 :class="{ 'is-invalid': errors.has('duration') }"
                 type="text"
                 class="form-control"
                 name="duration"
          >
          <small class="form-text text-muted">{{ durationObject.milliseconds() | duration('full') }}</small>
          <div class="invalid-feedback">{{ errors.first('duration') }}</div>
        </div>
      </div>

      <!-- Need to use v-html to insert formatted json (secured content) -->
      <!-- eslint-disable -->
      <div class="mt-3">
        <h4>{{ $t('g-spectrumsForm:durationFormatTitle') | capitalize }}</h4>
        <p v-html="$t('g-spectrumsForm:durationFormat')" />
        <p v-html="$t('g-spectrumsForm:durationMinimum')" />
        <p v-html="$t('g-spectrumsForm:durationMaximum')" />
        <p>{{ $t('g-spectrumsForm:durationExamples') }}</p>
        <ul>
          <li v-html="$t('g-spectrumsForm:durationExample1')" />
          <li v-html="$t('g-spectrumsForm:durationExample2')" />
          <li v-html="$t('g-spectrumsForm:durationExample3')" />
          <li v-html="$t('g-spectrumsForm:durationExample4')" />
        </ul>
      </div>
      <!-- eslint-enable -->
    </div>
  </modal>
</template>

<script>
import to from '@/modules/await-to-js'
import eventBus from '@/modules/eventBus'
import Duration from 'duration-js'

export default {
  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    // Display or hide modal
    // To be used as v-model
    show: {
      type: Boolean,
      required: true
    }
  },

  data () {
    return {
      // Display or hide b-modal
      // It cannot change our props, so we need another variable
      visible: false,
      // Modem list
      modems: [],
      // Form
      modem: null,
      duration: '90'
    }
  },

  computed: {
    durationObject () {
      // Check format
      let durationArray = this.duration.split(':')
      // Only seconds, add 0 hours and 0 minutes
      if (durationArray.length === 1) durationArray = [0, 0, ...durationArray]
      // Minutes and seconds, add 0 hours
      else if (durationArray.length === 2) durationArray = [0, ...durationArray]

      let duration = {}
      try {
        duration = new Duration(
          // Hours
          (durationArray[0] || 0) * 3600000 +
          // Minutes
          (durationArray[1] || 0) * 60000 +
          // Seconds
          (durationArray[2] || 0) * 1000
        )
      } catch (e) {
        // Unable to create duration, set not valid duration (must be > 15s)
        duration = new Duration(0)
      }

      return duration
    }
  },

  watch: {
    // Synchronize show and visible
    // Use change event to update v-model
    show () {
      this.reset()
      this.visible = this.show

      if (this.visible) {
        this.getModems()
      }
    },
    visible () {
      this.$emit('change', this.visible)
    }
  },

  methods: {
    async getModems () {
      let error, response
      [error, response] = await to(this.$api.getGatewayModems(this.$store.getters.gateway))
      if (error || !response) return

      this.modems = response.data.list
      if (this.modems.length === 1) this.modem = this.modems[0]
    },

    async reset () {
      this.modem = []
      this.duration = '90'

      // Reset form validation
      await this.$nextTick()
      this.$validator.reset()
    },

    async createSpectrum () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      let error
      [error] = await to(this.$api.createGatewayModemSpectrum(this.modem, this.durationObject.seconds()))
      if (error) return

      this.visible = false
      // Success
      eventBus.$emit('message-success', this.$t('g-spectrumsForm:createSuccess'))
      // Reset form
      this.reset()
      // Refresh list
      this.$emit('success')
    }
  }
}
</script>
